import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export type InsuranceVerificationStatus = 'verified' | 'manual_review_required' | 'provider_unconfigured' | 'failed'
export type CoverageStatus = 'active' | 'inactive' | 'unknown'
export type AuthorizationStatus = 'not_required' | 'required' | 'pending' | 'approved' | 'denied' | 'unknown'
export type InsuranceProviderMode = 'manual' | 'unconfigured' | 'external'
export type InsuranceAiSource = 'model' | 'heuristic_fallback'
export type InsuranceDenialRisk = 'low' | 'medium' | 'high'

export interface AuthorizationTimelineEntry {
  at: string
  status: AuthorizationStatus
  note: string | null
  source: 'verification' | 'workflow_update'
  actor_user_id: string | null
  actor_role: string | null
}

export interface InsuranceAiAssessment {
  source: InsuranceAiSource
  denial_risk: InsuranceDenialRisk
  denial_confidence: number
  patient_responsibility_estimate_cents: number | null
  responsibility_confidence: number
  recommended_next_step: string
  limitation_summary: string
  auth_letter_draft: string
  factors: string[]
}

export interface InsuranceVerificationRecord {
  insurance_verification_id: string
  visit_id: string
  payer_name: string | null
  verification_status: InsuranceVerificationStatus
  coverage_status: CoverageStatus
  auth_required: boolean | null
  auth_status: AuthorizationStatus
  provider_mode: InsuranceProviderMode
  provider_reference: string | null
  transaction_cost_cents: number | null
  benefits_summary: Record<string, unknown>
  verification_summary: Record<string, unknown>
  auth_workflow_note: string | null
  auth_follow_up_date: string | null
  auth_updated_at: string | null
  auth_updated_by: string | null
  auth_timeline: AuthorizationTimelineEntry[]
  ai_assessment: InsuranceAiAssessment | null
  ai_model_version: string | null
  ai_prompt_hash: string | null
  ai_input_hash: string | null
  ai_output_hash: string | null
  ai_generated_at: string | null
  ai_generated_by: string | null
  last_error_code: string | null
  last_error_message: string | null
  last_verified_at: string | null
  updated_at: string
}

export interface InsuranceDashboardVisit {
  visit_id: string
  facility_id: string
  facility_name: string
  visit_type: string
  survey_date: string
  visit_status: string
  verification: InsuranceVerificationRecord | null
}

export interface InsuranceDashboardResponse {
  provider_mode: InsuranceProviderMode
  window_days: number
  start_date: string
  end_date: string
  visits: InsuranceDashboardVisit[]
}

export interface InsuranceBatchVerifyResponse {
  provider_mode: InsuranceProviderMode
  processed_count: number
  visits: Array<{
    visit_id: string
    verification: InsuranceVerificationRecord | null
  }>
}

export interface InsuranceEligibilityInput {
  payerId?: string
  memberId?: string
  patientFirstName?: string
  patientLastName?: string
  patientDateOfBirth?: string
}

export interface UpdateInsuranceAuthWorkflowInput {
  visitId: string
  authStatus: AuthorizationStatus
  authRequired?: boolean | null
  authWorkflowNote?: string
  authFollowUpDate?: string
}

function parseAiAssessment(value: unknown): InsuranceAiAssessment | null {
  if (!asRecord(value)) return null
  const factors = Array.isArray(value.factors) ? value.factors.filter((entry): entry is string => typeof entry === 'string') : []
  if (!['model', 'heuristic_fallback'].includes(String(value.source))) return null
  if (!['low', 'medium', 'high'].includes(String(value.denial_risk))) return null

  return {
    source: value.source as InsuranceAiSource,
    denial_risk: value.denial_risk as InsuranceDenialRisk,
    denial_confidence: typeof value.denial_confidence === 'number' ? value.denial_confidence : 0,
    patient_responsibility_estimate_cents:
      typeof value.patient_responsibility_estimate_cents === 'number' ? value.patient_responsibility_estimate_cents : null,
    responsibility_confidence: typeof value.responsibility_confidence === 'number' ? value.responsibility_confidence : 0,
    recommended_next_step: typeof value.recommended_next_step === 'string' ? value.recommended_next_step : '',
    limitation_summary: typeof value.limitation_summary === 'string' ? value.limitation_summary : '',
    auth_letter_draft: typeof value.auth_letter_draft === 'string' ? value.auth_letter_draft : '',
    factors,
  }
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parseVerification(value: unknown): InsuranceVerificationRecord | null {
  if (!asRecord(value)) return null
  if (typeof value.insurance_verification_id !== 'string' || typeof value.visit_id !== 'string') return null
  return {
    insurance_verification_id: value.insurance_verification_id,
    visit_id: value.visit_id,
    payer_name: typeof value.payer_name === 'string' ? value.payer_name : null,
    verification_status: value.verification_status as InsuranceVerificationStatus,
    coverage_status: value.coverage_status as CoverageStatus,
    auth_required: typeof value.auth_required === 'boolean' ? value.auth_required : null,
    auth_status: value.auth_status as AuthorizationStatus,
    provider_mode: value.provider_mode as InsuranceProviderMode,
    provider_reference: typeof value.provider_reference === 'string' ? value.provider_reference : null,
    transaction_cost_cents: typeof value.transaction_cost_cents === 'number' ? value.transaction_cost_cents : null,
    benefits_summary: asRecord(value.benefits_summary) ? value.benefits_summary : {},
    verification_summary: asRecord(value.verification_summary) ? value.verification_summary : {},
    auth_workflow_note: typeof value.auth_workflow_note === 'string' ? value.auth_workflow_note : null,
    auth_follow_up_date: typeof value.auth_follow_up_date === 'string' ? value.auth_follow_up_date : null,
    auth_updated_at: typeof value.auth_updated_at === 'string' ? value.auth_updated_at : null,
    auth_updated_by: typeof value.auth_updated_by === 'string' ? value.auth_updated_by : null,
    auth_timeline: Array.isArray(value.auth_timeline)
      ? value.auth_timeline
          .filter((entry): entry is Record<string, unknown> => asRecord(entry))
          .map((entry) => ({
            at: typeof entry.at === 'string' ? entry.at : '',
            status: entry.status as AuthorizationStatus,
            note: typeof entry.note === 'string' ? entry.note : null,
            source: entry.source === 'workflow_update' ? 'workflow_update' : 'verification',
            actor_user_id: typeof entry.actor_user_id === 'string' ? entry.actor_user_id : null,
            actor_role: typeof entry.actor_role === 'string' ? entry.actor_role : null,
          }))
      : [],
    ai_assessment: parseAiAssessment(value.ai_assessment),
    ai_model_version: typeof value.ai_model_version === 'string' ? value.ai_model_version : null,
    ai_prompt_hash: typeof value.ai_prompt_hash === 'string' ? value.ai_prompt_hash : null,
    ai_input_hash: typeof value.ai_input_hash === 'string' ? value.ai_input_hash : null,
    ai_output_hash: typeof value.ai_output_hash === 'string' ? value.ai_output_hash : null,
    ai_generated_at: typeof value.ai_generated_at === 'string' ? value.ai_generated_at : null,
    ai_generated_by: typeof value.ai_generated_by === 'string' ? value.ai_generated_by : null,
    last_error_code: typeof value.last_error_code === 'string' ? value.last_error_code : null,
    last_error_message: typeof value.last_error_message === 'string' ? value.last_error_message : null,
    last_verified_at: typeof value.last_verified_at === 'string' ? value.last_verified_at : null,
    updated_at: typeof value.updated_at === 'string' ? value.updated_at : '',
  }
}

function parseVisit(value: unknown): InsuranceDashboardVisit {
  if (!asRecord(value)) throw new Error('Malformed insurance dashboard response from server')
  if (
    typeof value.visit_id !== 'string' ||
    typeof value.facility_id !== 'string' ||
    typeof value.facility_name !== 'string' ||
    typeof value.visit_type !== 'string' ||
    typeof value.survey_date !== 'string' ||
    typeof value.visit_status !== 'string'
  ) {
    throw new Error('Malformed insurance dashboard response from server')
  }
  return {
    visit_id: value.visit_id,
    facility_id: value.facility_id,
    facility_name: value.facility_name,
    visit_type: value.visit_type,
    survey_date: value.survey_date,
    visit_status: value.visit_status,
    verification: parseVerification(value.verification),
  }
}

export async function getInsuranceDashboard(facilityId?: string): Promise<InsuranceDashboardResponse> {
  const query = facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : ''
  const { data, error } = await invokeFunction<unknown>('insurance-verify', {
    method: 'GET',
    path: `/${query}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load insurance verification dashboard')
  if (!asRecord(data) || !Array.isArray(data.visits) || typeof data.provider_mode !== 'string' || typeof data.window_days !== 'number') {
    throw new Error('Malformed insurance dashboard response from server')
  }

  return {
    provider_mode: data.provider_mode as InsuranceProviderMode,
    window_days: data.window_days,
    start_date: typeof data.start_date === 'string' ? data.start_date : '',
    end_date: typeof data.end_date === 'string' ? data.end_date : '',
    visits: data.visits.map(parseVisit),
  }
}

export async function verifyInsuranceVisit(
  visitId: string,
  payerName?: string,
  eligibility?: InsuranceEligibilityInput,
): Promise<InsuranceVerificationRecord> {
  const { data, error } = await invokeFunction<unknown>('insurance-verify', {
    method: 'POST',
    path: '/verify',
    body: {
      visit_id: visitId,
      payer_name: payerName,
      payer_id: eligibility?.payerId,
      member_id: eligibility?.memberId,
      patient_first_name: eligibility?.patientFirstName,
      patient_last_name: eligibility?.patientLastName,
      patient_date_of_birth: eligibility?.patientDateOfBirth,
    },
  })

  if (error) throw toApiRequestError(error, 'Failed to verify insurance for this visit')
  if (!asRecord(data)) throw new Error('Malformed insurance verification response from server')
  const verification = parseVerification(data.verification)
  if (!verification) throw new Error('Malformed insurance verification response from server')
  return verification
}

export async function batchVerifyInsurance(facilityId?: string): Promise<InsuranceBatchVerifyResponse> {
  const { data, error } = await invokeFunction<unknown>('insurance-verify', {
    method: 'POST',
    path: '/batch-verify',
    body: facilityId ? { facility_id: facilityId } : {},
  })

  if (error) throw toApiRequestError(error, 'Failed to batch verify insurance')
  if (!asRecord(data) || typeof data.provider_mode !== 'string' || typeof data.processed_count !== 'number' || !Array.isArray(data.visits)) {
    throw new Error('Malformed insurance batch verification response from server')
  }

  return {
    provider_mode: data.provider_mode as InsuranceProviderMode,
    processed_count: data.processed_count,
    visits: data.visits.map((row) => {
      if (!asRecord(row) || typeof row.visit_id !== 'string') {
        throw new Error('Malformed insurance batch verification response from server')
      }
      return {
        visit_id: row.visit_id,
        verification: parseVerification(row.verification),
      }
    }),
  }
}

export async function updateInsuranceAuthWorkflow(input: UpdateInsuranceAuthWorkflowInput): Promise<InsuranceVerificationRecord> {
  const { data, error } = await invokeFunction<unknown>('insurance-verify', {
    method: 'POST',
    path: '/auth-workflow',
    body: {
      visit_id: input.visitId,
      auth_status: input.authStatus,
      auth_required: input.authRequired,
      auth_workflow_note: input.authWorkflowNote,
      auth_follow_up_date: input.authFollowUpDate,
    },
  })

  if (error) throw toApiRequestError(error, 'Failed to update insurance authorization workflow')
  if (!asRecord(data)) throw new Error('Malformed insurance authorization workflow response from server')
  const verification = parseVerification(data.verification)
  if (!verification) throw new Error('Malformed insurance authorization workflow response from server')
  return verification
}

export async function generateInsuranceAiAssist(visitId: string): Promise<InsuranceVerificationRecord> {
  const { data, error } = await invokeFunction<unknown>('insurance-verify', {
    method: 'POST',
    path: '/ai-assist',
    body: { visit_id: visitId },
  })

  if (error) throw toApiRequestError(error, 'Failed to generate insurance AI assist')
  if (!asRecord(data)) throw new Error('Malformed insurance AI assist response from server')
  const verification = parseVerification(data.verification)
  if (!verification) throw new Error('Malformed insurance AI assist response from server')
  return verification
}
